import React from 'react';
import {
  AUTH_MODAL_ROUTES,
  AUTH_OUTER_MODAL_ROUTES,
  AUTH_ROUTES,
  CONTAINER_MODAL_ROUTES,
} from './routes';
import {
  AuthModalStackParams,
  AuthOuterModalStackParams,
  AuthStackParams,
} from './types';
import {
  AuthStackContextProvider,
  BASE_HEADER_HEIGHT,
  useAuthStackContext,
} from '@/contexts/auth-stack-context';
import { AuthStackHeader } from '@/components/auth-stack-header';
import { BecomeCustomerScreen } from '@/screens/auth/become-customer-screen';
import { CatalogStack } from './partners/catalog-stack';
import { CountrySelectorScreen } from '@/screens/country-selector-screen';
import { createStackNavigator } from '@react-navigation/stack';
import { DummyAdsContextProvider } from '@/contexts/ads-context';
import { DummyItemLeadTimeContextProvider } from '@/contexts/item-lead-time-context';
import { DummyItemRecommendationsContextProvider } from '@/contexts/item-recommendations-context';
import { DummyNewOrderActionsContextProvider } from '@/contexts/new-order-actions-context';
import { DummyNewOrderContextProvider } from '@/contexts/new-order-context';
import { DummyOrderGuideItemsContextProvider } from '@/contexts/dummy-order-guide-items-context-provider';
import { DummyOrderGuideSettingsContextProvider } from '@/contexts/order-guide-settings-context';
import { DummyVariantPackInfoContextProvider } from '@/contexts/variant-pack-info-context';
import { DummyVolumePromotionsContextProvider } from '@/contexts/volume-promotions-context';
import {
  flexModalCardStyle,
  useModalStackScreenOptions,
} from '@/hooks/use-modal-stack-screen-options';
import { GuestUserContextProvider } from '@/contexts/user-context';
import { HeaderBackButton } from '@/components/header-back-button';
import { ItemDetailContextProvider } from '@/contexts/item-detail-context';
import { LoginRecoveryScreen } from '@/screens/auth/login-recovery-screen';
import { LogoLoadingIndicator } from '@/components/logo-loading-indicator';
import { OrderGuideStoreProvider } from '@/stores/order-guide-store';
import { PlacesSearchScreen } from '@/screens/places-search-screen';
import { SignInCodeScreen } from '@/screens/auth/sign-in-code-screen';
import { SignInScreen } from '@/screens/auth/sign-in-screen';
import { UpdateOrderGuideItemContextProvider } from '@/contexts/update-order-guide-item-context';
import { useDefaultStackScreenOptions } from '@/hooks/use-default-stack-screen-options';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WelcomeScreen } from '@/screens/auth/welcome-screen';

const OuterModalStack = createStackNavigator<AuthOuterModalStackParams>();
const ModalStack = createStackNavigator<AuthModalStackParams>();
const Stack = createStackNavigator<AuthStackParams>();

export const AuthStackContainer: React.FC = () => {
  return (
    <AuthStackContextProvider>
      <AuthStack />
    </AuthStackContextProvider>
  );
};

const AuthStackNavigator: React.FC = () => {
  const screenOptions = useDefaultStackScreenOptions();
  const { cmsEnabled } = useAuthStackContext();
  const { top } = useSafeAreaInsets();

  return (
    <Stack.Navigator
      initialRouteName={cmsEnabled ? AUTH_ROUTES.Welcome : AUTH_ROUTES.SignIn}
      screenOptions={{
        ...screenOptions,
        header: (props) => <AuthStackHeader {...props} />,
        headerBackImage: () => <HeaderBackButton />,
        headerBackTitleVisible: false,
        headerShown: true,
        headerStyle: {
          elevation: 0,
          height: BASE_HEADER_HEIGHT + top,
        },
        headerTitle: '',
        headerTransparent: true,
      }}
    >
      <Stack.Screen component={WelcomeScreen} name={AUTH_ROUTES.Welcome} />
      <Stack.Screen component={SignInScreen} name={AUTH_ROUTES.SignIn} />
      <Stack.Screen
        component={SignInCodeScreen}
        name={AUTH_ROUTES.SignInCode}
      />
      <Stack.Screen component={CatalogStack} name={AUTH_ROUTES.GuestCatalog} />
    </Stack.Navigator>
  );
};

// The auth screens navigate to CountrySelector / PlacesSearch, routes that
// normally live in ContainerStack. Experiences that mount the auth stack
// without ContainerStack (e.g. the sales portal when logged out) can't reach
// them, so register them here too. They sit in an OUTER modal stack, a level
// above the form modals, so a picker opened from a form (become-customer →
// address search) is the top of its own navigator and renders as a centered
// card instead of stacking flat over the form and stripping its layout.
const AuthModalStack: React.FC = () => {
  const modalScreenOptions = useModalStackScreenOptions();

  return (
    <ModalStack.Navigator
      initialRouteName={AUTH_MODAL_ROUTES.Base}
      screenOptions={modalScreenOptions}
    >
      <ModalStack.Screen
        component={AuthStackNavigator}
        name={AUTH_MODAL_ROUTES.Base}
      />
      <ModalStack.Screen
        component={BecomeCustomerScreen}
        name={AUTH_MODAL_ROUTES.BecomeCustomer}
      />
      <ModalStack.Screen
        component={LoginRecoveryScreen}
        name={AUTH_MODAL_ROUTES.LoginRecovery}
      />
    </ModalStack.Navigator>
  );
};

const AuthStack: React.FC = () => {
  const modalScreenOptions = useModalStackScreenOptions();
  const { whitelabelConfigLoading } = useAuthStackContext();

  if (whitelabelConfigLoading) {
    return <LogoLoadingIndicator />;
  }

  return (
    <OrderGuideStoreProvider>
      <GuestUserContextProvider>
        <DummyItemRecommendationsContextProvider>
          <DummyOrderGuideSettingsContextProvider>
            <DummyNewOrderContextProvider>
              <DummyNewOrderActionsContextProvider>
                <DummyVariantPackInfoContextProvider>
                  <DummyItemLeadTimeContextProvider>
                    <DummyVolumePromotionsContextProvider>
                      <DummyAdsContextProvider>
                        <DummyOrderGuideItemsContextProvider>
                          <UpdateOrderGuideItemContextProvider>
                            <ItemDetailContextProvider>
                              <OuterModalStack.Navigator
                                initialRouteName={AUTH_OUTER_MODAL_ROUTES.Base}
                                screenOptions={modalScreenOptions}
                              >
                                <OuterModalStack.Screen
                                  component={AuthModalStack}
                                  name={AUTH_OUTER_MODAL_ROUTES.Base}
                                />
                                <OuterModalStack.Screen
                                  component={CountrySelectorScreen}
                                  name={CONTAINER_MODAL_ROUTES.CountrySelector}
                                  options={{ cardStyle: flexModalCardStyle }}
                                />
                                <OuterModalStack.Screen
                                  component={PlacesSearchScreen}
                                  name={CONTAINER_MODAL_ROUTES.PlacesSearch}
                                  options={{ cardStyle: flexModalCardStyle }}
                                />
                              </OuterModalStack.Navigator>
                            </ItemDetailContextProvider>
                          </UpdateOrderGuideItemContextProvider>
                        </DummyOrderGuideItemsContextProvider>
                      </DummyAdsContextProvider>
                    </DummyVolumePromotionsContextProvider>
                  </DummyItemLeadTimeContextProvider>
                </DummyVariantPackInfoContextProvider>
              </DummyNewOrderActionsContextProvider>
            </DummyNewOrderContextProvider>
          </DummyOrderGuideSettingsContextProvider>
        </DummyItemRecommendationsContextProvider>
      </GuestUserContextProvider>
    </OrderGuideStoreProvider>
  );
};
